import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { dashboardRhService } from '../services/dashboard-rh.service';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DashboardIndicators } from '../components/DashboardIndicators';
import { DashboardCharts } from '../components/DashboardCharts';
import { useDashboardCalculations } from '../hooks/useDashboardCalculations';

export function PublicDashboardPage() {
  const { t } = useTranslation();
  const { dashboardUuid } = useParams();
  const [selectedEstablishment, setSelectedEstablishment] = useState<string | null>(null);

  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    error: dashboardError,
  } = useQuery({
    queryKey: ['dashboard-rh', 'public', dashboardUuid],
    queryFn: () => dashboardRhService.getPublicDashboard(dashboardUuid!),
    enabled: !!dashboardUuid,
  });

  const dashboard = dashboardData?.data;
  const { evolutions, lastMonth, formatCurrency, formatPercent, filteredIndicators, summary } =
    useDashboardCalculations(dashboard, selectedEstablishment);

  // Extract unique establishments
  const establishments = (dashboard?.monthlyIndicators
    ? Object.values(
        dashboard.monthlyIndicators.reduce((acc, m) => {
          if (m.establishmentCode && m.establishmentName) {
            acc[m.establishmentCode] = { code: m.establishmentCode, name: m.establishmentName };
          }
          return acc;
        }, {} as Record<string, { code: string; name: string }>)
      ).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : []);

  if (isLoadingDashboard) {
    return (
      <div className="mx-auto min-h-screen space-y-6 bg-neutral-50 p-6">
        <LoadingSkeleton className="h-8 w-64" />
        <LoadingSkeleton className="h-12 w-full" />
        <LoadingSkeleton className="h-96" />
      </div>
    );
  }

  console.log({dashboardError, dashboardData})

  if (dashboardError || !dashboardData || !dashboard || !lastMonth) {
    return (
      <div className="mx-auto min-h-screen space-y-6 bg-neutral-50 p-6">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-error-100 mb-4 rounded-full p-3">
            <AlertCircle className="text-error-600 h-6 w-6" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-neutral-900">
            {t('common.error', 'Erreur')}
          </h3>
          <p className="text-sm text-neutral-600">
            {t(
              'dashboardRh.page.error',
              'Impossible de charger le tableau de bord'
            )}
          </p>
        </div>
      </div>
    );
  }

  const { previousPeriodSummary } = dashboard;

  return (
    <div className="mx-auto min-h-screen space-y-6 bg-neutral-50 p-6">
      {/* Header */}
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 rounded-xl border border-neutral-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-neutral-900">
                {t('dashboardRh.page.title', 'Tableau de bord')}
              </h1>
              <p className="text-sm text-neutral-600">
                {dashboard.folderName} - {dashboard.startPeriod} à{' '}
                {dashboard.endPeriod}
              </p>
            </div>
            {/* Establishment Filter */}
            {establishments.length > 0 && (
              <div className="relative">
                <select
                  value={selectedEstablishment || ''}
                  onChange={(e) => setSelectedEstablishment(e.target.value || null)}
                  className="h-9 rounded-lg border border-neutral-300 bg-white px-3 py-1 text-sm text-neutral-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">{t('dashboardRh.filter.all', 'Tous les établissements')}</option>
                  {establishments.map((e) => (
                    <option key={e.code} value={e.code as string}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Key Indicators */}
        <DashboardIndicators
          summary={summary!}
          previousPeriodSummary={previousPeriodSummary}
          lastMonth={lastMonth}
          evolutions={evolutions}
          formatCurrency={formatCurrency}
          formatPercent={formatPercent}
        />

        {/* Charts */}
        <div className="mt-6">
          <DashboardCharts
            monthlyIndicators={filteredIndicators}
            lastMonth={lastMonth}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
          />
        </div>
      </div>
    </div>
  );
}
